import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Alert, Button, Card, Field, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { today, usePeopleForm } from './data';

/**
 * Solicitud de baja (guía 7.5): el jefe elige a uno de sus subordinados (RR. HH., a cualquiera de la organización) y
 * el formulario se autocompleta con sus datos y licencias. La aprueba RR. HH.
 */
export function NewSeparationPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [organizationId, setOrganizationId] = useState<string | undefined>(params.get('organizacion') ?? undefined);
  const form = usePeopleForm(organizationId);
  const [userId, setUserId] = useState(params.get('usuario') ?? '');
  const [lastDay, setLastDay] = useState(today());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const data = form.data;
  const person = data.separable.find((p) => p.id === userId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ number: number }>('/api/usuarios/bajas', { json: { userId, lastDay, reason: reason.trim() || null } });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      void navigate(`/usuarios/bajas/${created.number}`, { state: { created: true } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/usuarios/bajas" className="text-sm text-marca hover:underline">
          ← {t('us.bajas_titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('us.bajas_nueva')}</h1>
        <p className="text-sm text-slate-600">{data.isHr ? t('us.baja_ayuda_rrhh') : t('us.baja_ayuda_jefe')}</p>
      </div>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          {data.organizations.length > 1 && (
            <Select
              id="us-org"
              label={t('us.campo_linea')}
              value={data.organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setUserId('');
              }}
            >
              {data.organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.groupName} › {o.name}
                </option>
              ))}
            </Select>
          )}
          {data.separable.length === 0 ? (
            <Alert tone="info">{data.isHr ? t('us.sin_personas') : t('us.sin_subordinados')}</Alert>
          ) : (
            <Select id="us-persona" label={t('us.campo_persona')} required value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">{t('us.elegir_persona')}</option>
              {data.separable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName} ({p.email})
                </option>
              ))}
            </Select>
          )}
          {person && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" aria-label={t('us.datos_persona')}>
              <dt className="text-slate-500">{t('us.campo_departamento')}</dt>
              <dd>{person.department ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_cargo')}</dt>
              <dd>{person.position ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_licencias')}</dt>
              <dd>{person.licenses.length ? person.licenses.join(', ') : t('us.ninguna')}</dd>
            </dl>
          )}
          <Field id="us-ultimo-dia" type="date" label={t('us.campo_ultimo_dia')} required value={lastDay} onChange={(e) => setLastDay(e.target.value)} />
          <Textarea id="us-razon" label={t('us.campo_razon')} rows={3} maxLength={2_000} value={reason} onChange={(e) => setReason(e.target.value)} />
          <p className="text-xs text-slate-500">{t('us.baja_que_pasa')}</p>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !person || !lastDay}>
              {busy ? t('us.enviando') : t('us.enviar')}
            </Button>
            <Link to="/usuarios/bajas" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('comun.cancelar')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
